import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { MarketDepthVppaGridTemplate as template } from './market-depth-vppa-grid.template';
import { MarketDepthVppaGridStyles as styles } from './market-depth-vppa-grid.styles';
import { ZeroGridPro as zeroAgGrid } from '@genesislcap/foundation-zero-grid-pro';
import { INegotiationService, IVppaService } from '../../../../services';
import { logger, Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { updateArray } from '../../../../utils/dataserver';
import { IEnumService } from '../../../../services/enum.service';
import { CONTRACT_TYPE, PRODUCT_TYPE, TECHNOLOGY, VPPA_RFQ_STATUS } from '../../../../utils';
import { ICompanyService } from '../../../../services/company.service';

const name = 'market-depth-vppa-grid';

interface IMarketDepthVppaGrid {
  TICKER: string;
  UNIQUE_ID: string;
  TECHNOLOGY: string;
  NAME: string;
  ISO: string;
  SETTLEMENT_POINT: string;
  VOLUME: number;
  FIXED_PRICE: number;
  TENOR_START_DATE: number | Date;
  TENOR_MONTHS: number;
  SELLER_POST_COD_COLLATERAL: number;
  IMPLIED_CARBON_PRICE: number;
  VPPA_STATUS: string;
  VPPA_ID: string;
  RFQ_ID: string;
}

@customElement({
  name,
  template,
  styles,
})
export class MarketDepthVppaGrid extends FASTElement {
  @observable marketDepthVppaGrid: zeroAgGrid;
  @observable marketDepthVppaList: IMarketDepthVppaGrid[];
  @observable vppaSubscription: SocketSubscription;
  @observable negotiationSubscription: SocketSubscription;
  @observable vppaList: any[] = [];
  @observable negotiationList: any[] = [];

  @IVppaService vppaService: IVppaService;
  @INegotiationService negotiationService: INegotiationService;
  @IEnumService enumService: IEnumService;
  @ICompanyService companyService: ICompanyService;

  @attr @observable rfq: any;

  connectedCallback() {
    super.connectedCallback();

    const gridOptions = {
      animateRows: true,
      suppressRowClickSelection: true,
      rowClass: 'transparent-background',
      scrollbarWidth: 10,
      defaultColDef: {
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
    };

    this.marketDepthVppaGrid.gridOptions = gridOptions;

    this.vppaSubscription?.unsubscribe();

    this.vppaSubscription = this.vppaService
      .getAllVppas(
        `VPPA_STATUS != '${VPPA_RFQ_STATUS.EXECUTED}' && VPPA_STATUS != '${
          VPPA_RFQ_STATUS.DELETED
        }' &&  VPPA_STATUS != '${VPPA_RFQ_STATUS.DELISTED}' && CASCADE_COMPANY_ID== '${
          this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
        }'`,
      )
      .subscribe({
        next: (streamMessage: Message) => {
          this.vppaList = updateArray(streamMessage.ROW, this.vppaList)?.sort(
            (a, b) => a.MODIFIED_AT - b.MODIFIED_AT,
          );
        },
        error: (error: any) => {
          this.vppaList = [];
          logger.error(error);
        },
      });
  }

  public onGridReady() {
    this.marketDepthVppaList = [];
    this.marketDepthVppaGrid.gridApi.setRowData(this.marketDepthVppaList);
    this.marketDepthVppaGrid.gridApi.addEventListener('rowSelected', this.updateIsVppaSelected);
  }

  private updateIsVppaSelected = (e) => {
    const isVppaSelected =
      this.marketDepthVppaGrid.gridApi.getSelectedRows().length > 0 ? true : false;
    this.$emit('vppaSelected', isVppaSelected);
  };

  public rfqChanged(oldValue, newValue) {
    this.negotiationSubscription?.unsubscribe();
    if (!newValue) {
      this.marketDepthVppaList = [];
      this.marketDepthVppaGrid?.gridApi.setRowData(this.marketDepthVppaList);
      return;
    }
    this.negotiationList = [];
    this.negotiationSubscription = this.negotiationService
      .getAllNegotiations({
        CRITERIA_MATCH: `RFQ_ID=='${newValue.RFQ_ID}'`,
      })
      .subscribe({
        next: (streamMessage: Message) => {
          this.marketDepthVppaList = [];
          this.marketDepthVppaList.push({
            TICKER: newValue.RFQ_TICKER,
            UNIQUE_ID: newValue.RFQ_UNIQUE_ID,
            TECHNOLOGY: newValue.RFQ_TECHNOLOGY,
            NAME: '---',
            ISO: newValue.RFQ_ISO,
            SETTLEMENT_POINT: newValue.RFQ_SETTLEMENT_POINT,
            VOLUME: newValue.RFQ_VOLUME,
            FIXED_PRICE: newValue.FIXED_PRICE,
            TENOR_START_DATE: newValue.RFQ_TENOR_START_DATE,
            TENOR_MONTHS: newValue.RFQ_TENOR_MONTHS,
            SELLER_POST_COD_COLLATERAL: newValue.BUYER_POST_COD_COLLATERAL,
            IMPLIED_CARBON_PRICE: 0,
            VPPA_STATUS: '---',
            VPPA_ID: '',
            RFQ_ID: newValue.RFQ_ID,
          });
          this.negotiationList = updateArray(streamMessage.ROW, this.negotiationList);

          this.vppaList.forEach((vppa) => {
            if (!this.negotiationList.find((negotiation) => negotiation.VPPA_ID == vppa.VPPA_ID)) {
              if (
                vppa.ISO === newValue.RFQ_ISO &&
                vppa.CONTRACT_TYPE !== CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED &&
                (newValue.RFQ_TECHNOLOGY === TECHNOLOGY.BOTH ||
                  newValue.RFQ_TECHNOLOGY === vppa.TECHNOLOGY) &&
                vppa.VPPA_STATUS !== VPPA_RFQ_STATUS.AWAITING_KEY_METRICS &&
                vppa.PRODUCT_TYPE === newValue.RFQ_PRODUCT_TYPE &&
                vppa.CONTRACT_TYPE === newValue.RFQ_CONTRACT_TYPE &&
                (vppa.PRODUCT_TYPE === PRODUCT_TYPE.RECS ||
                  vppa.SETTLEMENT_POINT === newValue.RFQ_SETTLEMENT_POINT)
              ) {
                this.marketDepthVppaList.push(vppa);
              }
            }
          });
          this.marketDepthVppaGrid?.gridApi.setRowData(this.marketDepthVppaList);
        },
        error: (error: any) => {
          this.vppaList = [];
          logger.error(error);
        },
      });
  }

  public getSelectedRows(): Array<IMarketDepthVppaGrid> {
    return this.marketDepthVppaGrid.gridApi.getSelectedRows();
  }
}
